import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAsyncPaginatedData } from "../../../hooks/useAsyncPaginatedData";
import { PAGE_SIZE_OPTIONS, type ListPagination } from "../../../lib/pagination";
import { deleteBatchJob, listBatchJobs, type BatchJob } from "../../../lib/corpusApi";
import { ROUTES } from "../../../route_paths";

/** The instant one month before now. The list never shows jobs created before it. */
function oneMonthAgo(): string {
  const since = new Date();
  since.setMonth(since.getMonth() - 1);
  return since.toISOString();
}

export interface UseBatchJobsResult {
  jobs: BatchJob[];
  loading: boolean;
  /** A failed load, or the failure of the last delete. */
  error: string | null;
  pagination: ListPagination;
  /** Deletes a job (the UI calls it archiving), then reloads the list. */
  deleteJob: (job: BatchJob) => Promise<void>;
  openJob: (job: BatchJob) => void;
  refresh: () => void;
}

/** Paginated list of batch jobs created in the last month, newest first — drives the Batch jobs page. */
export function useBatchJobs(): UseBatchJobsResult {
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    items: jobs,
    loading,
    error: loadError,
    pagination: basePagination,
    refresh,
  } = useAsyncPaginatedData(
    (offsetPage) => listBatchJobs({ ...offsetPage, createdAfter: oneMonthAgo() }),
    { pageSize: rowsPerPage }
  );

  const pagination: ListPagination = {
    ...basePagination,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    onPageSizeChange: setRowsPerPage,
  };

  const deleteJob = useCallback(
    async (job: BatchJob) => {
      setActionError(null);
      try {
        await deleteBatchJob(job.id);
        await refresh();
      } catch (e) {
        setActionError(e instanceof Error ? e.message : "Could not delete the batch job.");
      }
    },
    [refresh]
  );

  const openJob = useCallback(
    (job: BatchJob) => {
      navigate(ROUTES.internal.batchJobs.detail.path({ id: job.id }));
    },
    [navigate]
  );

  return {
    jobs,
    loading,
    error: actionError ?? loadError,
    pagination,
    deleteJob,
    openJob,
    refresh,
  };
}
